/**
 * <load-more> — infinite scroll / "load more" for the collection product grid.
 *
 * Fetches the next page via the Section Rendering API (?section_id=&page=N) and
 * APPENDS the new <li> items into #product-grid (unlike facets.js which replaces).
 * Because ordering is resolved server-side in Liquid (DOM order == visual order),
 * appending the next page at the end is naturally correct — no reflow of prior items.
 *
 * Hybrid behavior: auto-loads the first `data-auto-load-limit` batches as the user
 * scrolls near the bottom, then requires a manual "load more" click so the footer
 * stays reachable.
 *
 * Lives inside #ProductGridContainer, so when facets.js replaces that container's
 * innerHTML on filter/sort, this element is torn down (disconnectedCallback fires,
 * observer disconnects) and a fresh <load-more> from the reset page 1 re-initializes.
 */
class LoadMore extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.autoLoaded = 0;
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.grid = document.getElementById('product-grid');
    this.button = this.querySelector('[data-load-more-button]');
    this.status = this.querySelector('.load-more__status');
    this.nextPage = parseInt(this.dataset.nextPage, 10) || 2;
    this.autoLoadLimit = parseInt(this.dataset.autoLoadLimit, 10);
    if (Number.isNaN(this.autoLoadLimit)) this.autoLoadLimit = 2;

    if (this.button) this.button.addEventListener('click', this.onClick);

    // Hide the numbered pagination fallback while JS load-more is active.
    const pagination = this.closest('.collection')?.querySelector('.pagination-wrapper');
    if (pagination) pagination.setAttribute('hidden', '');

    this.setupObserver();
  }

  disconnectedCallback() {
    // Critical: facets.js replaces #ProductGridContainer innerHTML on filter/sort,
    // which removes this element. Disconnect the observer to avoid leaks.
    if (this.observer) this.observer.disconnect();
    if (this.button) this.button.removeEventListener('click', this.onClick);
  }

  setupObserver() {
    if (!('IntersectionObserver' in window)) return;
    // Fire ~400px before the sentinel is visible so loading feels seamless on mobile.
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        if (this.autoLoaded >= this.autoLoadLimit) return;
        this.loadNext(true);
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this);
  }

  onClick(event) {
    event.preventDefault();
    this.loadNext(false);
  }

  async loadNext(isAuto) {
    if (this.loading) return;
    this.loading = true;
    this.classList.add('load-more--loading');
    if (this.button) this.button.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', this.nextPage);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Load more failed: ${response.status}`);
      const text = await response.text();

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const newItems = doc.querySelectorAll('#product-grid > li');

      if (newItems.length && this.grid) {
        const fragment = document.createDocumentFragment();
        newItems.forEach((li) => {
          // Match facets.js: skip the scroll-reveal entrance animation on injected items.
          li.classList.remove('scroll-trigger', 'animate--slide-in');
          fragment.appendChild(document.importNode(li, true));
        });
        this.grid.appendChild(fragment);
        if (this.status) this.status.textContent = `${newItems.length} productos más cargados`;
      }

      // The fetched section re-emits <load-more> only when another page exists.
      const nextEl = doc.querySelector('load-more');
      if (nextEl) {
        this.nextPage += 1;
        if (isAuto) this.autoLoaded += 1;
      } else {
        this.remove(); // no more pages → removes button + disconnects observer
        return;
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
      this.classList.remove('load-more--loading');
      if (this.button) this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('load-more')) {
  customElements.define('load-more', LoadMore);
}
